/** Administrador de reglas de despiece (SI condiciones ENTONCES componentes con fórmula y merma). */
import React, { useState, useEffect } from 'react';
import api from '@/api/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple, X } from '@phosphor-icons/react';

const OPS = ['=', '!=', '>', '>=', '<', '<=', 'in'];
const errMsg = (e, d) => e?.response?.data?.detail || d;
const condVacia = () => ({ variable: '', operador: '=', valor: '' });
const compVacio = () => ({ item_codigo: '', descripcion: '', formula_cantidad: '', unidad: 'unidad', tipo: 'material', merma_pct: '', costo_unitario: '' });

export const ReglasAdmin = ({ familias, onClose }) => {
  const [reglas, setReglas] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [f, setF] = useState(null);
  const [editId, setEditId] = useState(null);

  const load = () => api.get('/sa/reglas').then((r) => setReglas(r.data.reglas || [])).catch(() => {});
  useEffect(() => { load(); api.get('/sa/catalogo').then((r) => setCatalogo(r.data.items || [])).catch(() => {}); }, []);

  const abrir = (r) => {
    setEditId(r?.id || null);
    setF(r ? {
      nombre: r.nombre, familia: r.familia, activa: r.activa !== false, notas: r.notas || '',
      condiciones: (r.condiciones || []).map((c) => ({ ...condVacia(), ...c })),
      componentes: (r.componentes || []).map((c) => ({ ...compVacio(), ...c, merma_pct: c.merma_pct ?? '', costo_unitario: c.costo_unitario ?? '' })),
    } : { nombre: '', familia: familias[0]?.v || '', activa: true, notas: '', condiciones: [condVacia()], componentes: [compVacio()] });
  };

  const guardar = async () => {
    if (!f.nombre) return toast.error('Escribe el nombre de la regla');
    const body = {
      nombre: f.nombre, familia: f.familia, activa: !!f.activa, notas: f.notas,
      condiciones: f.condiciones.filter((c) => c.variable).map((c) => ({ variable: c.variable, operador: c.operador, valor: c.valor })),
      componentes: f.componentes.filter((c) => c.item_codigo || c.descripcion).map((c) => ({
        item_codigo: c.item_codigo, descripcion: c.descripcion, formula_cantidad: c.formula_cantidad,
        unidad: c.unidad || 'unidad', tipo: c.tipo || 'material',
        merma_pct: c.merma_pct === '' ? null : Number(c.merma_pct),
        costo_unitario: Number(c.costo_unitario || 0),
      })),
    };
    try {
      if (editId) await api.put(`/sa/reglas/${editId}`, body);
      else await api.post('/sa/reglas', body);
      toast.success(editId ? 'Regla actualizada' : 'Regla creada');
      setF(null); setEditId(null); load();
    } catch (e) { toast.error(errMsg(e, 'No se pudo guardar')); }
  };

  const borrar = async (r) => {
    if (!window.confirm(`¿Eliminar la regla "${r.nombre}"?`)) return;
    try { await api.delete(`/sa/reglas/${r.id}`); load(); } catch (e) { toast.error(errMsg(e, 'Error')); }
  };

  const setRow = (key, i, campo, val) => {
    const arr = [...f[key]];
    arr[i] = { ...arr[i], [campo]: val };
    setF({ ...f, [key]: arr });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[760px] max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Reglas de despiece</DialogTitle></DialogHeader>

        {!f ? (
          <>
            <Button size="sm" onClick={() => abrir(null)} className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white w-fit" data-testid="reglas-nueva">
              <Plus size={14} className="mr-1" /> Nueva regla
            </Button>
            <div className="border border-[var(--border)] rounded-lg divide-y divide-[var(--border)] max-h-[55vh] overflow-y-auto">
              {reglas.length === 0 ? <p className="p-4 text-sm text-[var(--text-muted)] text-center">Sin reglas técnicas. Las familias sin reglas mostrarán "Regla técnica pendiente".</p>
                : reglas.map((r) => (
                  <div key={r.id} className="flex items-center justify-between px-3 py-2 text-sm gap-2" data-testid={`reglas-item-${r.id}`}>
                    <div className="min-w-0">
                      <p className="font-bold truncate">{r.nombre}
                        {r.activa === false && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">inactiva</span>}
                      </p>
                      <p className="text-[11px] text-[var(--text-muted)] truncate">
                        {r.familia} · SI {(r.condiciones || []).map((c) => `${c.variable} ${c.operador} ${c.valor}`).join(' Y ') || '(siempre)'} → {(r.componentes || []).length} componente(s)
                      </p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => abrir(r)}><PencilSimple size={14} /></Button>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-red-400" onClick={() => borrar(r)}><Trash size={14} /></Button>
                    </div>
                  </div>
                ))}
            </div>
          </>
        ) : (
          <div className="space-y-3" data-testid="reglas-form">
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2"><label className="text-xs font-bold text-[var(--text-muted)]">Nombre *</label>
                <Input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} className="h-9" data-testid="reglas-f-nombre" /></div>
              <div><label className="text-xs font-bold text-[var(--text-muted)]">Familia</label>
                <select value={f.familia} onChange={(e) => setF({ ...f, familia: e.target.value })} className="w-full h-9 text-sm border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-2" data-testid="reglas-f-familia">
                  {familias.map((x) => <option key={x.v} value={x.v}>{x.label}</option>)}
                </select></div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <p className="text-xs font-black uppercase tracking-wide text-[var(--text-muted)]">SI (condiciones — vacío = siempre)</p>
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setF({ ...f, condiciones: [...f.condiciones, condVacia()] })} data-testid="reglas-add-cond">
                  <Plus size={13} /> Condición
                </Button>
              </div>
              {f.condiciones.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-1 items-center mt-1">
                  <Input value={c.variable} onChange={(e) => setRow('condiciones', i, 'variable', e.target.value)} placeholder="variable (ej. calibre_lamina)" className="col-span-5 h-8 text-xs" data-testid={`reglas-cond-var-${i}`} />
                  <select value={c.operador} onChange={(e) => setRow('condiciones', i, 'operador', e.target.value)} className="col-span-2 h-8 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1">
                    {OPS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <Input value={c.valor} onChange={(e) => setRow('condiciones', i, 'valor', e.target.value)} placeholder="valor" className="col-span-4 h-8 text-xs" data-testid={`reglas-cond-val-${i}`} />
                  <button type="button" className="col-span-1 text-red-400 flex justify-center" onClick={() => setF({ ...f, condiciones: f.condiciones.filter((_, j) => j !== i) })}><X size={14} /></button>
                </div>
              ))}
            </div>

            <div>
              <div className="flex items-center justify-between">
                <p className="text-xs font-black uppercase tracking-wide text-[var(--text-muted)]">ENTONCES agregar componentes</p>
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setF({ ...f, componentes: [...f.componentes, compVacio()] })} data-testid="reglas-add-comp">
                  <Plus size={13} /> Componente
                </Button>
              </div>
              {f.componentes.map((c, i) => (
                <div key={i} className="border border-[var(--border)] rounded-lg p-2 mt-1.5 grid grid-cols-12 gap-1 items-center">
                  <select value={c.item_codigo} onChange={(e) => {
                    const cat = catalogo.find((x) => x.codigo === e.target.value);
                    const arr = [...f.componentes];
                    arr[i] = { ...arr[i], item_codigo: e.target.value, descripcion: cat ? cat.nombre : arr[i].descripcion, unidad: cat ? cat.unidad : arr[i].unidad, tipo: cat ? cat.tipo : arr[i].tipo };
                    setF({ ...f, componentes: arr });
                  }} className="col-span-3 h-8 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1" data-testid={`reglas-comp-cat-${i}`}>
                    <option value="">Sin catálogo…</option>
                    {catalogo.map((x) => <option key={x.codigo} value={x.codigo}>{x.codigo} — {x.nombre}</option>)}
                  </select>
                  <Input value={c.descripcion} onChange={(e) => setRow('componentes', i, 'descripcion', e.target.value)} placeholder="Descripción" className="col-span-4 h-8 text-xs" data-testid={`reglas-comp-desc-${i}`} />
                  <select value={c.tipo} onChange={(e) => setRow('componentes', i, 'tipo', e.target.value)} className="col-span-2 h-8 text-xs border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-1">
                    <option value="material">Material</option><option value="mano_obra">M. Obra</option><option value="otro">Otro</option>
                  </select>
                  <Input value={c.unidad} onChange={(e) => setRow('componentes', i, 'unidad', e.target.value)} placeholder="Unidad" className="col-span-2 h-8 text-xs" />
                  <button type="button" className="col-span-1 text-red-400 flex justify-center" onClick={() => setF({ ...f, componentes: f.componentes.filter((_, j) => j !== i) })}><X size={14} /></button>
                  <Input value={c.formula_cantidad} onChange={(e) => setRow('componentes', i, 'formula_cantidad', e.target.value)} placeholder="Fórmula cantidad (ej. area * 1.05 ó cantidad_canales)" className="col-span-6 h-8 text-xs font-mono" data-testid={`reglas-comp-formula-${i}`} />
                  <Input type="number" value={c.merma_pct} onChange={(e) => setRow('componentes', i, 'merma_pct', e.target.value)} placeholder="Merma %" className="col-span-3 h-8 text-xs" data-testid={`reglas-comp-merma-${i}`} />
                  {!c.item_codigo && <Input type="number" value={c.costo_unitario} onChange={(e) => setRow('componentes', i, 'costo_unitario', e.target.value)} placeholder="Costo Q" className="col-span-3 h-8 text-xs" data-testid={`reglas-comp-costo-${i}`} />}
                </div>
              ))}
              <p className="text-[10px] text-[var(--text-muted)] mt-1">Variables disponibles en fórmulas: medidas (ancho_m, largo_m…), variables numéricas de la familia, area y cantidad. Funciones: min, max, round, ceil, floor, abs, sqrt.</p>
            </div>

            <label className="flex items-center gap-1.5 text-sm cursor-pointer">
              <input type="checkbox" checked={f.activa} onChange={(e) => setF({ ...f, activa: e.target.checked })} className="accent-[#0EA5E9]" /> Activa
            </label>
            <div className="flex gap-2">
              <Button onClick={guardar} className="flex-1 bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="reglas-f-guardar">
                {editId ? 'Guardar cambios' : 'Crear regla'}
              </Button>
              <Button variant="outline" onClick={() => { setF(null); setEditId(null); }}>Cancelar</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
